/**
 * Money.
 *
 * HARD RULE for this codebase: money is an INTEGER count of minor currency
 * units, always paired with a currency code and an exponent. No float ever
 * touches a price, a total, a cost or a payroll figure.
 *
 *   VND  exponent 0  ->  25000 minor  = 25.000 ₫
 *   USD  exponent 2  ->  25000 minor  = $250.00
 *
 * Percentages are basis points (bp): 10000 bp = 100%, 500 bp = 5%.
 */

export interface Money {
  readonly amountMinor: number;
  readonly currency: string;
  readonly exponent: number;
}

export const BP_SCALE = 10_000;

export function money(amountMinor: number, currency = 'VND', exponent = 0): Money {
  assertSafeInteger(amountMinor, 'amountMinor');
  return { amountMinor, currency, exponent };
}

export function zero(currency = 'VND', exponent = 0): Money {
  return { amountMinor: 0, currency, exponent };
}

function assertSafeInteger(n: number, label: string): void {
  if (!Number.isInteger(n)) throw new TypeError(`money: ${label} must be an integer, got ${n}`);
  if (!Number.isSafeInteger(n)) throw new RangeError(`money: ${label} exceeds safe integer range`);
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency || a.exponent !== b.exponent) {
    throw new TypeError(`money: currency mismatch ${a.currency}/${a.exponent} vs ${b.currency}/${b.exponent}`);
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amountMinor + b.amountMinor, a.currency, a.exponent);
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amountMinor - b.amountMinor, a.currency, a.exponent);
}

export function sum(items: readonly Money[], currency = 'VND', exponent = 0): Money {
  return items.reduce<Money>((acc, m) => add(acc, m), zero(currency, exponent));
}

/**
 * Multiply by an integer quantity. Quantities that are not whole (sell-by-
 * weight) are expressed in milli-units and must use `multiplyMilli`.
 */
export function multiply(m: Money, quantity: number): Money {
  assertSafeInteger(quantity, 'quantity');
  return money(m.amountMinor * quantity, m.currency, m.exponent);
}

/** quantityMilli = 1500 means 1.5 units. Rounds half-up to the minor unit. */
export function multiplyMilli(m: Money, quantityMilli: number): Money {
  assertSafeInteger(quantityMilli, 'quantityMilli');
  return money(divRoundHalfUp(m.amountMinor * quantityMilli, 1000), m.currency, m.exponent);
}

/** Apply a basis-point rate, e.g. 5% VAT = applyBp(total, 500). */
export function applyBp(m: Money, bp: number): Money {
  assertSafeInteger(bp, 'bp');
  return money(divRoundHalfUp(m.amountMinor * bp, BP_SCALE), m.currency, m.exponent);
}

/** Percentage discount: 10% off => discountBp(total, 1000). */
export function discountBp(m: Money, bp: number): Money {
  return subtract(m, applyBp(m, bp));
}

/** Integer division rounding half away from zero — deterministic, no floats. */
export function divRoundHalfUp(numerator: number, denominator: number): number {
  if (denominator === 0) throw new RangeError('money: division by zero');
  const sign = Math.sign(numerator) * Math.sign(denominator);
  const n = Math.abs(numerator);
  const d = Math.abs(denominator);
  const magnitude = Math.floor((n + Math.floor(d / 2)) / d);
  // Normalise negative zero: `-1 * 0` is `-0`, which is === 0 but NOT
  // Object.is-equal to it. A `-0` escaping into a score, a points total or a
  // JSON column is a value nobody expects to compare unequal to zero.
  return magnitude === 0 ? 0 : sign * magnitude;
}

/**
 * Split an amount into `parts` shares that add back up to exactly the original
 * (largest-remainder). Used for splitting a bill N ways without losing a dong.
 */
export function splitEvenly(m: Money, parts: number): Money[] {
  if (!Number.isInteger(parts) || parts <= 0) throw new RangeError('money: parts must be >= 1');
  const base = Math.trunc(m.amountMinor / parts);
  let remainder = m.amountMinor - base * parts;
  const out: Money[] = [];
  const step = remainder >= 0 ? 1 : -1;
  for (let i = 0; i < parts; i++) {
    let share = base;
    if (remainder !== 0) {
      share += step;
      remainder -= step;
    }
    out.push(money(share, m.currency, m.exponent));
  }
  return out;
}

/** Split proportionally to weights, preserving the total exactly. */
export function allocate(m: Money, weights: readonly number[]): Money[] {
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) return weights.map(() => zero(m.currency, m.exponent));
  const raw = weights.map((w) => Math.trunc((m.amountMinor * w) / total));
  let remainder = m.amountMinor - raw.reduce((a, b) => a + b, 0);
  const order = weights
    .map((w, i) => ({ i, frac: (m.amountMinor * w) / total - raw[i]! }))
    .sort((a, b) => b.frac - a.frac);
  for (const { i } of order) {
    if (remainder === 0) break;
    raw[i] = raw[i]! + Math.sign(remainder);
    remainder -= Math.sign(remainder);
  }
  return raw.map((a) => money(a, m.currency, m.exponent));
}

/** Round a total to the nearest `stepMinor` (VND cash rounding to 1.000₫). */
export function roundToStep(m: Money, stepMinor: number): Money {
  if (stepMinor <= 1) return m;
  return money(divRoundHalfUp(m.amountMinor, stepMinor) * stepMinor, m.currency, m.exponent);
}

const FORMATTERS = new Map<string, Intl.NumberFormat>();

/** Display only. Never feed a formatted string back into a calculation. */
export function formatMoney(m: Money, locale = 'vi-VN'): string {
  const key = `${locale}|${m.currency}|${m.exponent}`;
  let f = FORMATTERS.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: m.currency,
      minimumFractionDigits: m.exponent,
      maximumFractionDigits: m.exponent,
    });
    FORMATTERS.set(key, f);
  }
  return f.format(m.amountMinor / 10 ** m.exponent);
}
